import { SavedBookmarkSVG } from '@modules/common/components/SVGIcons';
import { useIsCollectionsEnabled } from '@modules/common/hooks';
import { AudioHighlightChip } from '@modules/feedback/components/AudioType/AudioFeedbackPlayer/AudioHighlightChip';
import {
  AudioHighlightMetadata,
  AudioHighlightMetadataType,
  KeywordSummaryAudioHighlightMetadata,
  ReasonSummaryAudioHighlightMetadata,
} from '@modules/feedback/components/AudioType/types';
import {
  getAudioSnippetSourceURL,
  getBookmarkStyles,
  getHighlightTooltipComponentProps,
  getLabelFromHighlight,
  getSummaryHighlightMetadataMatches,
  getSummaryTextIndexFromHighlightMetadataMatches,
} from '@modules/feedback/components/AudioType/utils';
import { FeedbackListViewType } from '@modules/feedback/constants';
import { useIsSnippetOrFeedbackSaved } from '@modules/savedFeedbackList/hooks';
import { OpenInNewRounded } from '@mui/icons-material';
import BookmarkRoundedIcon from '@mui/icons-material/BookmarkRounded';
import {
  Box,
  Button,
  Link,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/system';
import { IntegrationChannelType } from 'generated/graphql';
import { FeedbackLabel } from 'v2/core/entities/feedback';
import { UUID } from 'v2/core/entities/uuid';

interface AudioSummaryBookmarkProps {
  highlight:
    | KeywordSummaryAudioHighlightMetadata
    | ReasonSummaryAudioHighlightMetadata;
  highlightMetadata: AudioHighlightMetadata[];
  duration: number;
  onClick: (startTime: number) => void;
  externalSourceLink: string | undefined;
  sourceName: string;
  integrationChannelType: IntegrationChannelType | undefined;
  tenantID: UUID;
  tenantName: string;
  isDisabled?: boolean;
  selectedLabels: FeedbackLabel[];
  feedbackID: UUID;
  isFeedbackCardView: boolean;
  feedbackListViewType: FeedbackListViewType;
}

export const AudioSummaryHighlightBookmark = ({
  highlight,
  highlightMetadata,
  duration,
  onClick,
  externalSourceLink,
  sourceName,
  integrationChannelType,
  tenantID,
  tenantName,
  isDisabled = false,
  selectedLabels,
  feedbackID,
  isFeedbackCardView,
  feedbackListViewType,
}: AudioSummaryBookmarkProps) => {
  const theme = useTheme();

  const isSavedSnippet = useIsSnippetOrFeedbackSaved({
    feedbackID,
    snippetID: highlight.summaryText,
    labelID: highlight.labelID,
    unitIndex: highlight.summaryIndex,
  });

  const isCollectionsEnabled = useIsCollectionsEnabled();

  const highlightMatches = getSummaryHighlightMetadataMatches(
    highlightMetadata,
    highlight,
  ).filter(
    (match) =>
      selectedLabels.find((label) => label.id === match.labelID) ||
      isSavedSnippet,
  );

  const sourceSnippetURL = getAudioSnippetSourceURL(
    externalSourceLink,
    integrationChannelType,
    highlight.startTime,
    highlight.endTime,
    tenantName,
    tenantID,
  );

  const backgroundColor = theme.palette.secondary.main;

  const bookmarkStyles = getBookmarkStyles({
    theme,
    highlight,
    duration,
    zIndex: 1,
    isDisabled,
    backgroundColor,
  });

  const savedBookmarkBorderColor = theme.palette.warning.main;

  const isReasonSummaryType =
    highlight.type === AudioHighlightMetadataType.ReasonSummary;

  const shouldShowBookmarkHighlight = selectedLabels.find(
    (label) => label.id === highlight.labelID,
  );

  const bookmarkHighlight = shouldShowBookmarkHighlight ? (
    <Box
      onClick={() => {
        if (isDisabled) {
          return;
        }
        onClick(highlight.startTime);
      }}
      sx={bookmarkStyles}
    />
  ) : (
    <></>
  );

  const savedBookmarkHighlight = (
    <Box
      onClick={() => {
        if (isDisabled) {
          return;
        }
        onClick(highlight.startTime);
      }}
      sx={{
        ...bookmarkStyles,
        backgroundColor: 'transparent',
        border: `2px solid ${savedBookmarkBorderColor}`,
        padding: '2px',
        borderRadius: '2px',
        boxSizing: 'content-box',
        '&:hover': {
          backgroundColor: 'transparent',
        },
      }}
    >
      {shouldShowBookmarkHighlight ? (
        <Box
          sx={{
            backgroundColor: alpha(
              backgroundColor,
              isReasonSummaryType ? 1 : 0.8,
            ),
            height: '100%',
          }}
        />
      ) : null}
      {!isCollectionsEnabled && (
        <Stack
          style={{
            position: 'absolute',
            top: isReasonSummaryType ? '-65%' : '-95%',
            left: '95%',
          }}
        >
          <SavedBookmarkSVG stroke="#35343f" />
        </Stack>
      )}
    </Box>
  );

  if (
    isFeedbackCardView &&
    feedbackListViewType === FeedbackListViewType.SAVED &&
    !isSavedSnippet
  ) {
    return null;
  }

  return (
    <Tooltip
      arrow
      title={
        <Stack spacing={1} p={1}>
          <Stack spacing={1.5}>
            {highlightMatches.length > 1 && (
              <Typography variant="body2">Summary for:</Typography>
            )}
            {highlightMatches.map((highlightMatch, index) => {
              const label = getLabelFromHighlight(
                selectedLabels,
                highlightMatch,
              );
              return (
                <Stack
                  key={`${highlightMatch.labelID}_${index}_${highlightMatch.summaryIndex}`}
                  spacing={1}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {highlightMatches.length === 1 && (
                      <Typography variant="body2">Summary for</Typography>
                    )}
                    <Stack direction="row" spacing={1} alignItems="center">
                      {isSavedSnippet &&
                        highlightMatch.labelID === highlight.labelID && (
                          <BookmarkRoundedIcon
                            sx={{
                              color: 'warning.main',
                            }}
                          />
                        )}
                      <AudioHighlightChip
                        highlight={highlightMatch}
                        maxWidth={300}
                        label={label}
                      />
                    </Stack>
                    {highlightMatch.summaryCount > 1 && (
                      <Typography variant="caption">
                        ( {highlightMatch.summaryIndex + 1} /{' '}
                        {highlightMatch.summaryCount} )
                      </Typography>
                    )}
                  </Stack>
                  {index ===
                    getSummaryTextIndexFromHighlightMetadataMatches(
                      highlightMatches,
                      highlightMatch.summaryText,
                    ) && (
                    <Typography variant="caption">
                      {highlightMatch.summaryText}
                    </Typography>
                  )}
                </Stack>
              );
            })}
          </Stack>
          {sourceSnippetURL && (
            <Box>
              <Button
                size="small"
                LinkComponent={Link}
                href={sourceSnippetURL}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<OpenInNewRounded fontSize="small" />}
                sx={{
                  color: (theme) => `${theme.palette.primary.main} !important`,
                }}
              >
                Open In {sourceName || 'Source'}
              </Button>
            </Box>
          )}
        </Stack>
      }
      placement="top"
      componentsProps={getHighlightTooltipComponentProps()}
    >
      {isSavedSnippet ? savedBookmarkHighlight : bookmarkHighlight}
    </Tooltip>
  );
};
